import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Zap, Shield, BarChart3, Users, CheckCircle, Brain, Target, Clock, Award, Mail, MapPin, Linkedin, Globe, Instagram } from 'lucide-react';
import DemoRequestDialog from '../../components/DemoRequestDialog';
import EnterpriseRequestDialog from '../../components/EnterpriseRequestDialog';
import PublicLanguageSwitcher from '../../components/PublicLanguageSwitcher';

const LandingPage: React.FC = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('home');
  const [showDemoDialog, setShowDemoDialog] = useState(false);
  const [showEnterpriseDialog, setShowEnterpriseDialog] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const navigate = useNavigate();
  const yearlyDiscountRate = 0.3;

  const handlePlanSignup = (plan: string) => {
    const searchParams = new URLSearchParams({ plan });
    if (billingCycle === 'yearly') {
      searchParams.set('billing_cycle', 'yearly');
    }
    navigate(`/onboarding?${searchParams.toString()}`);
  };

  const pricingPlans = [
    {
      nameKey: 'landing.pricing.starter.name',
      monthlyPrice: 122,
      descKey: 'landing.pricing.starter.description',
      featuresKeys: [
        'landing.pricing.starter.features.0',
        'landing.pricing.starter.features.1',
        'landing.pricing.starter.features.2',
        'landing.pricing.starter.features.3',
        'landing.pricing.starter.features.4',
      ],
      ctaKey: 'landing.pricing.cta',
      ctaAction: () => handlePlanSignup('starter'),
      highlighted: false,
    },
    {
      nameKey: 'landing.pricing.professional.name',
      monthlyPrice: 317,
      descKey: 'landing.pricing.professional.description',
      featuresKeys: [
        'landing.pricing.professional.features.0',
        'landing.pricing.professional.features.1',
        'landing.pricing.professional.features.2',
        'landing.pricing.professional.features.3',
        'landing.pricing.professional.features.4',
        'landing.pricing.professional.features.5',
      ],
      ctaKey: 'landing.pricing.cta',
      ctaAction: () => handlePlanSignup('professional'),
      highlighted: true,
      badgeKey: 'landing.pricing.mostPopular',
    },
    {
      nameKey: 'landing.pricing.scale.name',
      monthlyPrice: 610,
      descKey: 'landing.pricing.scale.description',
      featuresKeys: [
        'landing.pricing.scale.features.0',
        'landing.pricing.scale.features.1',
        'landing.pricing.scale.features.2',
        'landing.pricing.scale.features.3',
        'landing.pricing.scale.features.4',
        'landing.pricing.scale.features.5',
      ],
      ctaKey: 'landing.pricing.cta',
      ctaAction: () => handlePlanSignup('scale'),
      highlighted: false,
    },
    {
      nameKey: 'landing.pricing.enterprise.name',
      customPriceKey: 'landing.pricing.enterprise.price',
      descKey: 'landing.pricing.enterprise.description',
      featuresKeys: [
        'landing.pricing.enterprise.features.0',
        'landing.pricing.enterprise.features.1',
        'landing.pricing.enterprise.features.2',
        'landing.pricing.enterprise.features.3',
        'landing.pricing.enterprise.features.4',
        'landing.pricing.enterprise.features.5',
      ],
      ctaKey: 'landing.pricing.contactSales',
      ctaAction: () => setShowEnterpriseDialog(true),
      highlighted: false,
    },
  ];

  const formatUsd = (amount: number) =>
    `$${amount.toLocaleString(undefined, {
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    })}`;

  const getPlanPrice = (plan: { monthlyPrice?: number; customPriceKey?: string }) => {
    if (!plan.monthlyPrice) {
      return t(plan.customPriceKey || 'landing.pricing.enterprise.price', 'Custom');
    }

    if (billingCycle === 'yearly') {
      const yearlyAmount = Math.round(plan.monthlyPrice * 12 * (1 - yearlyDiscountRate) * 100) / 100;
      return formatUsd(yearlyAmount);
    }

    return formatUsd(plan.monthlyPrice);
  };

  const getOriginalYearlyPrice = (monthlyPrice?: number) => {
    if (!monthlyPrice) return '';
    return formatUsd(monthlyPrice * 12);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-lotus-cream via-white to-lotus-gold/15">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white/80 backdrop-blur-md sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-4 group">
              <img 
                src="/logo.png" 
                alt={t('brand.name')} 
                className="w-20 h-20 object-contain group-hover:scale-105 transition-transform"
              />
              <div>
                <span className="text-3xl font-extrabold text-gray-900 tracking-tight block">
                  {t('brand.name')}
                </span>
                <span className="text-sm uppercase tracking-[0.3em] text-lotus-gold font-semibold">
                  {t('nav.tagline')}
                </span>
              </div>
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <a href="#features" className="text-gray-700 hover:text-lotus-gold font-medium transition-colors">
                {t('nav.features')}
              </a>
              <a href="#pricing" className="text-gray-700 hover:text-lotus-gold font-medium transition-colors">
                {t('nav.pricing')}
              </a>
              <a href="#about" className="text-gray-700 hover:text-lotus-gold font-medium transition-colors">
                {t('nav.about')}
              </a>
              <PublicLanguageSwitcher />
              <Link to="/login" className="text-gray-700 hover:text-lotus-gold font-medium">
                {t('nav.login')}
              </Link>
              <button onClick={() => setShowDemoDialog(true)} className="btn-primary">
                {t('landing.hero.demo')}
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center fade-in">
          <div className="relative w-48 h-48 mx-auto mb-10">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-lotus-gold via-amber-200 to-white blur-3xl opacity-60 animate-pulse"></div>
            <div className="relative w-full h-full flex items-center justify-center">
              <img 
                src="/logo.png" 
                alt="LotusRecruiter" 
                className="w-48 h-48 object-contain drop-shadow-2xl relative z-10"
              />
            </div>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            {t('landing.hero.title')}
            <span className="block text-lotus-gold mt-2">{t('landing.hero.subtitle')}</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            {t('landing.hero.description')}
          </p>
          <div className="flex gap-4 justify-center">
            <button onClick={() => setShowDemoDialog(true)} className="btn-primary text-lg px-8 py-3">
              {t('landing.hero.demo')}
            </button>
            <a href="#features" className="btn-outline text-lg px-8 py-3">
              {t('landing.hero.learnMore')}
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('landing.features.title')}</h2>
          <p className="text-xl text-gray-600">{t('landing.features.subtitle')}</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              icon: Zap,
              titleKey: 'landing.features.cvScreening.title',
              descKey: 'landing.features.cvScreening.description'
            },
            {
              icon: Users,
              titleKey: 'landing.features.aiInterviews.title',
              descKey: 'landing.features.aiInterviews.description'
            },
            {
              icon: Shield,
              titleKey: 'landing.features.antiCheating.title',
              descKey: 'landing.features.antiCheating.description'
            },
            {
              icon: BarChart3,
              titleKey: 'landing.features.analytics.title',
              descKey: 'landing.features.analytics.description'
            },
            {
              icon: CheckCircle,
              titleKey: 'landing.features.smartFiltering.title',
              descKey: 'landing.features.smartFiltering.description'
            },
            {
              icon: Users,
              titleKey: 'landing.features.multiTenant.title',
              descKey: 'landing.features.multiTenant.description'
            },
            {
              icon: CheckCircle,
              titleKey: 'landing.features.caseStudies.title',
              descKey: 'landing.features.caseStudies.description'
            },
            {
              icon: Brain,
              titleKey: 'landing.features.hrSimulations.title',
              descKey: 'landing.features.hrSimulations.description'
            },
            {
              icon: Target,
              titleKey: 'landing.features.videoQa.title',
              descKey: 'landing.features.videoQa.description'
            }
          ].map((feature, index) => (
            <div key={index} className="card fade-in">
              <feature.icon className="text-lotus-gold mb-4" size={40} />
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t(feature.titleKey)}</h3>
              <p className="text-gray-600">{t(feature.descKey)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('landing.pricing.title')}</h2>
          <p className="text-xl text-gray-600">{t('landing.pricing.subtitle')}</p>
        </div>

        <div className="flex justify-center mb-10">
          <div className="inline-flex items-center bg-white p-1 rounded-xl border border-gray-200 shadow-sm">
            <button
              type="button"
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                billingCycle === 'monthly' ? 'bg-lotus-cream/70 text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('landing.pricing.monthly')}
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle('yearly')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-2 ${
                billingCycle === 'yearly' ? 'bg-lotus-cream/70 text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('landing.pricing.annually')}
              <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full uppercase tracking-wider font-bold">
                %30 {t('common.discount')}
              </span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-8">
          {pricingPlans.map((plan, index) => {
            const cardClasses = plan.highlighted
              ? 'card border-2 border-lotus-gold bg-gradient-to-br from-lotus-gold/5 to-amber-50 relative'
              : 'card border-2 border-gray-200 hover:border-lotus-gold transition-all';

            return (
              <div key={index} className={cardClasses}>
                {plan.badgeKey && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-lotus-gold text-white px-4 py-1 rounded-full text-sm font-semibold">
                    {t(plan.badgeKey)}
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{t(plan.nameKey)}</h3>
                  <div className="text-4xl font-bold text-lotus-gold mb-2">
                    {getPlanPrice(plan)}
                  </div>
                  {plan.monthlyPrice && (
                    <p className="text-sm text-gray-500 mb-1">
                      / {billingCycle === 'yearly' ? t('landing.pricing.annually') : t('landing.pricing.monthly')}
                    </p>
                  )}
                  {billingCycle === 'yearly' && plan.monthlyPrice && (
                    <p className="text-xs text-green-700 font-medium">
                      {t('landing.pricing.yearlySavings', {
                        discount: '%30',
                        discountLabel: t('common.discount'),
                        original: getOriginalYearlyPrice(plan.monthlyPrice),
                      })}
                    </p>
                  )}
                  <p className="text-gray-600">{t(plan.descKey)}</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.featuresKeys.map((featureKey) => (
                    <li key={featureKey} className="flex items-center gap-2 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      {t(featureKey)}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={plan.ctaAction}
                  className={plan.highlighted ? 'btn-primary w-full text-center' : 'btn-outline w-full text-center'}
                >
                  {t(plan.ctaKey)}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('landing.about.title')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('landing.about.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">{t('landing.about.missionTitle')}</h3>
              <p className="text-lg text-gray-600 mb-4">
                {t('landing.about.missionText1')}
              </p>
              <p className="text-lg text-gray-600">
                {t('landing.about.missionText2')}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="card text-center">
                <Target className="w-12 h-12 text-lotus-gold mx-auto mb-3" />
                <div className="text-3xl font-bold text-gray-900 mb-1">10K+</div>
                <div className="text-gray-600">{t('landing.about.stats.interviews')}</div>
              </div>
              <div className="card text-center">
                <Users className="w-12 h-12 text-lotus-gold mx-auto mb-3" />
                <div className="text-3xl font-bold text-gray-900 mb-1">500+</div>
                <div className="text-gray-600">{t('landing.about.stats.companies')}</div>
              </div>
              <div className="card text-center">
                <Clock className="w-12 h-12 text-lotus-gold mx-auto mb-3" />
                <div className="text-3xl font-bold text-gray-900 mb-1">70%</div>
                <div className="text-gray-600">{t('landing.about.stats.timeSaved')}</div>
              </div>
              <div className="card text-center">
                <Award className="w-12 h-12 text-lotus-gold mx-auto mb-3" />
                <div className="text-3xl font-bold text-gray-900 mb-1">95%</div>
                <div className="text-gray-600">{t('landing.about.stats.satisfaction')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-lotus-gold py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            {t('landing.cta.title')}
          </h2>
          <p className="text-xl text-lotus-cream mb-8">
            {t('landing.cta.description')}
          </p>
          <Link to="/onboarding" className="btn-secondary text-lg px-8 py-3 bg-white text-lotus-gold hover:bg-lotus-cream">
            {t('landing.cta.button')}
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            {/* Company Info */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="/logo.png" 
                  alt={t('brand.name')} 
                  className="w-12 h-12 object-contain"
                />
                <span className="text-2xl font-bold">{t('brand.name')}</span>
              </div>
              <p className="text-gray-400 mb-6 max-w-md">
                {t('landing.footer.description')}
              </p>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/company/aiotech-ai-digital-solutions/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-lotus-gold transition-colors">
                  <Linkedin className="w-6 h-6" />
                </a>
                <a href="https://www.instagram.com/aiotech.ai/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-lotus-gold transition-colors">
                  <Instagram className="w-6 h-6" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-bold mb-4">{t('landing.footer.product')}</h3>
              <ul className="space-y-2">
                <li>
                  <a href="#features" className="text-gray-400 hover:text-white transition-colors">
                    {t('nav.features')}
                  </a>
                </li>
                <li>
                  <a href="#pricing" className="text-gray-400 hover:text-white transition-colors">
                    {t('nav.pricing')}
                  </a>
                </li>
                <li>
                  <button onClick={() => setShowDemoDialog(true)} className="text-gray-400 hover:text-white transition-colors text-left">
                    {t('landing.footer.requestDemo')}
                  </button>
                </li>
                <li>
                  <Link to="/login" className="text-gray-400 hover:text-white transition-colors">
                    {t('nav.login')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Policies */}
            <div>
              <h3 className="text-lg font-bold mb-4">{t('landing.footer.legal')}</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/legal/about" className="text-gray-400 hover:text-white transition-colors">
                    {t('landing.footer.about')}
                  </Link>
                </li>
                <li>
                  <Link to="/legal/privacy" className="text-gray-400 hover:text-white transition-colors">
                    {t('landing.footer.privacy')}
                  </Link>
                </li>
                <li>
                  <Link to="/legal/delivery" className="text-gray-400 hover:text-white transition-colors">
                    {t('landing.footer.delivery')}
                  </Link>
                </li>
                <li>
                  <Link to="/legal/distance-sales" className="text-gray-400 hover:text-white transition-colors">
                    {t('landing.footer.distanceSales')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-lg font-bold mb-4">{t('landing.footer.contact')}</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2 text-gray-400">
                  <Globe className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <a
                    href="http://www.aiotech.tr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors"
                  >
                    aiotech.tr
                  </a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <a
                    href={`mailto:${t('contact.mainEmail')}`}
                    className="hover:text-white transition-colors"
                  >
                    {t('contact.mainEmail')}
                  </a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <a
                    href={`mailto:${t('contact.salesEmail')}`}
                    className="hover:text-white transition-colors"
                  >
                    {t('contact.salesEmail')}
                  </a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>{t('contact.mainLocation')}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm">
                {t('landing.footer.copyright')}
              </p>
              <div className="flex flex-wrap gap-3 text-sm text-gray-400">
                <Link to="/legal/privacy" className="hover:text-white transition-colors">{t('landing.footer.privacyShort')}</Link>
                <Link to="/legal/distance-sales" className="hover:text-white transition-colors">{t('landing.footer.distanceSalesShort')}</Link>
                <Link to="/legal/delivery" className="hover:text-white transition-colors">{t('landing.footer.deliveryShort')}</Link>
                <Link to="/legal/about" className="hover:text-white transition-colors">{t('landing.footer.aboutShort')}</Link>
                <a href="http://www.aiotech.tr" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">{t('landing.footer.parentCompany')}</a>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-4 text-gray-300">
              <div className="flex items-center gap-2 bg-white text-blue-700 font-black tracking-widest px-3 py-1 rounded">
                <span>VISA</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 rounded-full bg-red-500 inline-block"></span>
                  <span className="w-4 h-4 rounded-full bg-orange-400 inline-block -ml-1"></span>
                </span>
                <span className="uppercase text-sm font-semibold">Mastercard</span>
              </div>
              <img
                src="/pay_with_iyzico_horizontal_colored.png"
                alt={t('landing.securePaymentAlt')}
                className="h-10 object-contain"
              />
            </div>
          </div>
        </div>
      </footer>

      {/* Demo Request Dialog */}
      <DemoRequestDialog
        isOpen={showDemoDialog}
        onClose={() => setShowDemoDialog(false)} 
      />
      <EnterpriseRequestDialog
        isOpen={showEnterpriseDialog}
        onClose={() => setShowEnterpriseDialog(false)}
      />
    </div>
  );
};

export default LandingPage;
